import { useEffect, useRef } from 'react';

interface Props {
  stream: MediaStream | null;
  name: string;
  muted?: boolean;
  isLocal?: boolean;
  /** L'utilisateur a coupé sa caméra → on affiche un avatar. */
  videoOff?: boolean;
  screen?: boolean;
  micOff?: boolean;
  /** Identifie l'animateur de la réunion (badge couronne). */
  isHost?: boolean;
  /** Participant en train de présenter son écran. */
  presenting?: boolean;
}

/**
 * Vignette vidéo d'un participant ZCall. Le flux média est directement
 * rattaché à l'élément <video> (aucun re-rendu inutile).
 * Taille « medium » : la grille qui la contient décide du nombre de colonnes.
 */
export default function VideoTile({
  stream, name, muted, isLocal, videoOff, screen, micOff, isHost, presenting,
}: Props) {
  const ref = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (el.srcObject !== stream) el.srcObject = stream;
    if (stream) {
      // Les navigateurs peuvent bloquer la lecture automatique d'un flux
      // distant (surtout avec le son) : on la relance explicitement.
      const played = el.play();
      if (played && typeof played.catch === 'function') played.catch(() => { /* clic utilisateur requis */ });
    }
  }, [stream]);

  return (
    <div
      className={`group relative rounded-lg overflow-hidden aspect-[4/3] flex items-center justify-center bg-foreground-950 ring-1 transition-colors ${
        presenting ? 'ring-accent-500' : 'ring-white/10 hover:ring-white/25'
      }`}
    >
      <video
        ref={ref}
        autoPlay
        playsInline
        muted={muted}
        className="w-full h-full object-cover"
        onClick={(e) => {
          const el = e.currentTarget;
          if (el.paused) void el.play().catch(() => {});
        }}
      />
      {videoOff && (
        <div className="absolute inset-0 flex items-center justify-center bg-foreground-950">
          <div className="w-11 h-11 rounded-full bg-white/10 flex items-center justify-center">
            <i className="ri-user-line text-white/70 text-lg"></i>
          </div>
        </div>
      )}
      <div className="absolute bottom-1.5 left-1.5 right-1.5 flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-black/55 backdrop-blur-sm max-w-[calc(100%-12px)]">
        {micOff && <i className="ri-mic-off-line text-red-400 text-[10px] flex-shrink-0"></i>}
        {screen && <i className="ri-computer-line text-white/70 text-[10px] flex-shrink-0"></i>}
        {isHost && <i className="ri-vip-crown-2-fill text-amber-400 text-[10px] flex-shrink-0"></i>}
        <span className="text-[10px] md:text-[11px] text-white font-medium truncate">
          {name}{isLocal ? ' (vous)' : ''}
        </span>
      </div>
      {isHost && !presenting && (
        <span className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded-full bg-amber-400/90 text-foreground-950 text-[9px] font-bold uppercase tracking-wide">
          Hôte
        </span>
      )}
      {presenting && (
        <span className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded-full bg-accent-500 text-background-50 text-[9px] font-bold uppercase tracking-wide">
          Présente
        </span>
      )}
    </div>
  );
}